import { env } from '@/config/env';
import {
  ApiError as AppApiError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  NotFoundError,
  ServerError,
  TimeoutError,
} from '@/lib/api/types/errors';

const API_BASE_URL = env.api.baseUrl;

/**
 * Default timeout for API requests (from env config)
 */
const DEFAULT_TIMEOUT = env.api.timeout;

/**
 * Maximum number of retry attempts (from env config)
 */
const MAX_RETRIES = env.api.maxRetries;

/**
 * HTTP status codes that should trigger a retry
 */
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Configuration options for API requests
 */
export interface ApiRequestConfig extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

/**
 * Enhanced API Client with comprehensive error handling
 *
 * Features:
 * - Automatic retry logic with exponential backoff
 * - Request timeout handling
 * - Detailed error typing and messages
 * - Network error detection
 * - Proper HTTP status code handling
 */
export class ApiClient {
  private baseUrl: string;
  private defaultTimeout: number;

  constructor(baseUrl: string = API_BASE_URL, timeout: number = DEFAULT_TIMEOUT) {
    this.baseUrl = baseUrl;
    this.defaultTimeout = timeout;
  }

  /**
   * Creates an AbortController with timeout
   */
  private createTimeoutController(timeout: number): {
    controller: AbortController;
    timeoutId: NodeJS.Timeout;
  } {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    return { controller, timeoutId };
  }

  /**
   * Calculates exponential backoff delay
   */
  private getRetryDelay(attempt: number, baseDelay = 1000): number {
    return Math.min(baseDelay * 2 ** attempt, 10000);
  }

  /**
   * Determines if an error is retryable
   */
  private isRetryableError(error: Error, statusCode?: number): boolean {
    // Network errors are retryable
    if (error instanceof NetworkError || error.name === 'TypeError') {
      return true;
    }

    // Certain status codes are retryable
    if (statusCode && RETRYABLE_STATUS_CODES.includes(statusCode)) {
      return true;
    }

    return false;
  }

  /**
   * Parses error response body
   */
  private async parseErrorResponse(response: Response): Promise<string> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        return (
          errorData.message ||
          errorData.error?.message ||
          (typeof errorData.error === 'string' ? errorData.error : null) ||
          JSON.stringify(errorData)
        );
      }
      return await response.text();
    } catch {
      return `HTTP ${response.status}: ${response.statusText}`;
    }
  }

  /**
   * Throws appropriate error based on response status
   */
  private async handleErrorResponse(response: Response, endpoint: string): Promise<never> {
    const statusCode = response.status;
    const errorMessage = await this.parseErrorResponse(response);

    switch (statusCode) {
      case 401:
        // Dispatch auth logout event to clear session
        window.dispatchEvent(new CustomEvent('auth-logout'));
        throw new AuthenticationError(errorMessage);
      case 403:
        throw new AuthorizationError(errorMessage);
      case 404:
        throw new NotFoundError(errorMessage, endpoint);
      case 408:
        throw new TimeoutError(errorMessage, endpoint);
      case 422:
        // Validation error from server
        throw new AppApiError(errorMessage, statusCode, endpoint, {
          validationErrors: true,
        });
      case 500:
      case 502:
      case 503:
      case 504:
        throw new ServerError(errorMessage, statusCode, endpoint);
      default:
        throw new AppApiError(errorMessage, statusCode, endpoint);
    }
  }

  /**
   * Core request method with retry logic and error handling
   */
  private async request<T>(
    endpoint: string,
    options: ApiRequestConfig = {},
    retryCount = 0,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const timeout = options.timeout || this.defaultTimeout;
    const maxRetries = options.retries ?? MAX_RETRIES;

    // Create timeout controller
    const { controller, timeoutId } = this.createTimeoutController(timeout);

    const config: RequestInit = {
      ...options,
      signal: controller.signal,
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      if (!response.ok) {
        await this.handleErrorResponse(response, endpoint);
      }

      // Handle empty responses (204 No Content, etc.)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new TimeoutError(`Request to ${endpoint} timed out after ${timeout}ms`, endpoint);
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new NetworkError(
          'Network request failed. Please check your internet connection and try again.',
        );
      }

      // If it's already one of our custom errors, rethrow
      if (error instanceof AppApiError || error instanceof NetworkError) {
        // Retry logic for retryable errors
        if (retryCount < maxRetries && this.isRetryableError(error, error.statusCode)) {
          const delay = this.getRetryDelay(retryCount, options.retryDelay);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return this.request<T>(endpoint, options, retryCount + 1);
        }

        throw error;
      }

      // Unknown error
      throw new AppApiError(
        error instanceof Error ? error.message : 'An unknown error occurred',
        undefined,
        endpoint,
      );
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown, options?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown, options?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown, options?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: ApiRequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
